import React, { useState } from 'react';
import { Modal, Button, Carousel, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faBed, 
  faBath, 
  faRuler, 
  faHeart,
  faShare,
  faMapMarkerAlt,
  faCalendar,
  faPhone,
  faEnvelope,
  faBuilding,
  faHome,
  faDollarSign,
  faCheck,
  faPrint,
  faPlus,
  faRoute
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import './PropertyDetailsModal.css';
import { Property } from '../../types/property';
import { isPropertyAddress, safeFormatPrice, getAddressString, getCity, getState } from '../../types/propertyUtils';

interface PropertyDetailsModalProps {
  show: boolean;
  property: Property | null;
  onHide: () => void;
  onSave?: (property: Property) => void;
  onScheduleTour?: (property: Property) => void;
  isSaved?: boolean;
  isInTour?: boolean;
}

const PropertyDetailsModal: React.FC<PropertyDetailsModalProps> = ({
  show,
  property,
  onHide,
  onSave,
  onScheduleTour,
  isSaved = false,
  isInTour = false
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  if (!property) return null;

  // Debug logging
  console.log('PropertyDetailsModal - property data:', property);
  console.log('PropertyDetailsModal - description:', property.description);
  console.log('PropertyDetailsModal - images:', property.images);
  console.log('PropertyDetailsModal - all property keys:', Object.keys(property));

  const propertyImages = Array.isArray(property.images) ? property.images : [];
  const hasImages = propertyImages.length > 0;


  const handleSave = () => {
    if (onSave) {
      onSave(property);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${getAddressString(property)} - ${safeFormatPrice(property.price)}`,
        text: `Check out this property: ${property.bedrooms} bed, ${property.bathrooms} bath in ${getCity(property)}`,
        url: window.location.href
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="property-details-modal"
    >
      <Modal.Header className="border-0 pb-0">
        <div className="modal-header-content">
          <div>
            <h2 className="modal-price">{safeFormatPrice(property.price)}</h2>
            <p className="modal-address">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
              {getAddressString(property)}
            </p>
          </div>
          <Button variant="close" onClick={onHide}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="pt-3">
        <Row>
          {/* Left Column - Images */}
          <Col lg={7}>
            {hasImages ? (
              <div className="image-gallery-section">
                <Carousel 
                  activeIndex={activeImageIndex} 
                  onSelect={setActiveImageIndex}
                  interval={null}
                >
                  {propertyImages.map((image, index) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100 gallery-image"
                        src={image}
                        alt={`${getAddressString(property)} - Image ${index + 1}`}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
                
                {/* Thumbnail Strip */}
                {propertyImages.length > 1 && (
                  <div className="thumbnail-strip">
                    {propertyImages.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                        onClick={() => setActiveImageIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="no-image-placeholder">
                <FontAwesomeIcon icon={faHome} size="3x" />
                <p>No images available</p>
              </div>
            )}

            {/* Property Description */}
            {property.description && (
              <div className="property-section">
                <h4>Property Description</h4>
                <p className="property-description">{property.description}</p>
              </div>
            )}

            {/* Features */}
            {property.features && Array.isArray(property.features) && property.features.length > 0 && (
              <div className="property-section">
                <h4>Features & Amenities</h4>
                <Row className="features-grid">
                  {property.features.map((feature, index) => (
                    <Col xs={6} md={4} key={index} className="feature-item">
                      <FontAwesomeIcon icon={faCheck} className="feature-icon" />
                      {feature}
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Col>

          {/* Right Column - Details */}
          <Col lg={5}>
            {/* Quick Stats */}
            <div className="quick-stats">
              <div className="stat-item">
                <FontAwesomeIcon icon={faBed} className="stat-icon" />
                <div>
                  <div className="stat-value">{property.bedrooms || 0}</div>
                  <div className="stat-label">Bedrooms</div>
                </div>
              </div>
              <div className="stat-item">
                <FontAwesomeIcon icon={faBath} className="stat-icon" />
                <div>
                  <div className="stat-value">{property.bathrooms || 0}</div>
                  <div className="stat-label">Bathrooms</div>
                </div>
              </div>
              <div className="stat-item">
                <FontAwesomeIcon icon={faRuler} className="stat-icon" />
                <div>
                  <div className="stat-value">{(property.sqft || property.squareFeet)?.toLocaleString() || 'N/A'}</div>
                  <div className="stat-label">Sq Ft</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <Button 
                variant={isSaved ? "danger" : "outline-primary"} 
                className="action-btn"
                onClick={handleSave}
              >
                <FontAwesomeIcon icon={isSaved ? faHeart : faHeartRegular} className="me-2" />
                {isSaved ? 'Saved' : 'Save'}
              </Button>
              <Button 
                variant="outline-primary" 
                className="action-btn"
                onClick={handleShare}
              >
                <FontAwesomeIcon icon={faShare} className="me-2" />
                Share
              </Button>
              <Button 
                variant="outline-primary" 
                className="action-btn"
                onClick={handlePrint}
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </Button>
            </div>

            {onScheduleTour && (
              <Button 
                variant={isInTour ? "success" : "primary"}
                size="lg" 
                className="w-100 schedule-tour-btn"
                onClick={() => {
                  onScheduleTour(property);
                  // Log property added to tour
                  const tourItem = {
                    propertyMLS: property.mlsNumber,
                    propertyAddress: getAddressString(property),
                    price: property.price,
                    bedrooms: property.bedrooms,
                    bathrooms: property.bathrooms,
                    sqft: property.sqft,
                    addedAt: new Date().toISOString()
                  };
                  console.log(isInTour ? 'Property removed from tour:' : 'Property added to tour:', tourItem);
                }}
              >
                <FontAwesomeIcon icon={isInTour ? faCheck : faPlus} className="me-2" />
                {isInTour ? 'Added to Tour' : 'Add to Tour'}
              </Button>
            )}

            {/* Build Your Tour Message */}
            {!isInTour && onScheduleTour && (
              <div className="tour-message">
                <FontAwesomeIcon icon={faRoute} className="tour-icon" />
                <p>Just pick the homes you want to see - no pressure, no sales pitch</p>
              </div>
            )}

            {/* Property Details */}
            <div className="property-details-section">
              <h5>Property Details</h5>
              <div className="detail-row">
                <span className="detail-label">MLS #</span>
                <span className="detail-value">{property.mlsNumber}</span>
              </div>
              {property.propertyType && (
                <div className="detail-row">
                  <span className="detail-label">Property Type</span>
                  <span className="detail-value">{property.propertyType}</span>
                </div>
              )}
              {property.yearBuilt && (
                <div className="detail-row">
                  <span className="detail-label">Year Built</span>
                  <span className="detail-value">{property.yearBuilt}</span>
                </div>
              )}
              {property.daysOnMarket !== undefined && (
                <div className="detail-row">
                  <span className="detail-label">Days on Market</span>
                  <span className="detail-value">
                    <FontAwesomeIcon icon={faCalendar} className="me-1" />
                    {property.daysOnMarket} days
                  </span>
                </div>
              )}
              {property.pricePerSqft && (
                <div className="detail-row">
                  <span className="detail-label">Price per Sq Ft</span>
                  <span className="detail-value">
                    <FontAwesomeIcon icon={faDollarSign} />
                    {property.pricePerSqft}
                  </span>
                </div>
              )}
              {property.lotSize && (
                <div className="detail-row">
                  <span className="detail-label">Lot Size</span>
                  <span className="detail-value">{property.lotSize?.toLocaleString() || '0'} sq ft</span>
                </div>
              )}
            </div>

            {/* KairosIQ AI Realty Section */}
            <div className="listing-info-section connect-realty-section">
              <div className="courtesy-header">
                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                Ready to Show You Around
              </div>
              <div className="agent-details">
                <p className="agent-message">
                  Pick all the homes you want to see. When you're ready, we'll set up tours at your convenience - no obligations.
                </p>
                <p className="listing-courtesy">
                  <small>Listing courtesy of {property.listingBrokerage || property.listingOffice || property.listingAgent?.officeName || 'MLS Partner'}</small>
                </p>
              </div>
            </div>

            {/* Fair Housing */}
            <div className="fair-housing-section">
              <svg 
                className="fair-housing-logo" 
                viewBox="0 0 100 50" 
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Equal Housing Opportunity"
              >
                <path d="M25 5 L5 20 L5 35 L20 35 L20 25 L30 25 L30 35 L45 35 L45 20 Z" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"/>
                <path d="M25 5 L45 20 M25 5 L5 20" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"/>
                <line x1="50" y1="18" x2="60" y2="18" stroke="currentColor" strokeWidth="2"/>
                <line x1="50" y1="25" x2="60" y2="25" stroke="currentColor" strokeWidth="2"/>
                <text x="25" y="45" 
                      textAnchor="middle" 
                      fontSize="8" 
                      fill="currentColor"
                      fontFamily="Arial, sans-serif">
                  EQUAL HOUSING OPPORTUNITY
                </text>
              </svg>
            </div>
          </Col>
        </Row>
        
        {/* MLS Attribution */}
        <div className="mls-attribution mt-4 p-3 bg-light rounded" style={{
          fontSize: '11px',
          color: '#6c757d',
          textAlign: 'center'
        }}>
          <p className="mb-1">
            Listing information provided courtesy of KairosIQ AI Realty, Inc. 
            The data relating to real estate for sale on this website comes in part from the 
            Internet Data Exchange (IDX) Program of Arizona Regional Multiple Listing Service, Inc.
          </p>
          {/* ARMLS IDX 23.3.4 — VERBATIM disclaimer (July 2026 audit cure) */}
          <p className="mb-1">
            All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.
          </p>
          <p className="mb-0">
            © {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc. All rights reserved.
          </p>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PropertyDetailsModal;